import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ChevronLeft, ChevronRight, Flag, Heart, MapPin, MessageCircle, MessageSquare,
  Phone, Share2, ShieldCheck, Tag, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dateTR, mediaUrl, money } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { ListingCard, StatusPill } from "@/components/ListingCard";
import { VerificationBadges, VerificationNote } from "@/components/VerificationBadges";
import { COMPLAINT_TYPES, ROLE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { SignupGateDialog } from "@/components/SignupGate";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const SPECS = [
  ["listing_no", "İlan No"], ["listing_type", "İlan Türü"], ["breed", "Irk"],
  ["sub_breed", "Alt Irk"], ["sex", "Cinsiyet"], ["age_months", "Yaş (ay)"],
  ["quantity", "Adet"], ["live_weight", "Canlı Ağırlık (kg)"], ["avg_weight", "Ortalama Ağırlık (kg)"],
  ["min_weight", "Min Ağırlık (kg)"], ["max_weight", "Maks Ağırlık (kg)"],
  ["breeding_status", "Damızlık Durumu"], ["pregnancy_month", "Gebelik Ayı"],
  ["milk_yield", "Süt Verimi (lt/gün)"], ["fattening_status", "Besi Durumu"],
  ["vaccination_status", "Aşı Durumu"], ["rearing_type", "Yetiştirme Şekli"],
  ["tag_number", "Küpe No"], ["sale_reason", "Satış Nedeni"], ["price_type", "Fiyat Tipi"],
];

const BOOLS = [
  ["has_tag", "Küpe"], ["has_passport", "Pasaport"], ["health_certificate", "Sağlık Belgesi"],
  ["transport_available", "Nakliye"], ["pregnant", "Gebe"], ["negotiable", "Pazarlık"],
  ["sacrifice_suitable", "Kurbanlığa Uygun (satıcı beyanı)"],
];

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [gate, setGate] = useState(null);
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [idx, setIdx] = useState(0);
  const [showPhone, setShowPhone] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [msg, setMsg] = useState("Merhaba, ilanınızla ilgilenip bilgi almak istiyorum.");
  const [offer, setOffer] = useState({ price: "", quantity: 1, note: "", delivery_date: "", transport_preference: "" });
  const [report, setReport] = useState({ type: COMPLAINT_TYPES[0], description: "" });

  const load = () => api.get(`/listings/${id}`).then((r) => {
    setL(r.data);
    trackEvent("LISTING_VIEWED", {}, id);
  }).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    setIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!l)
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8">
        <div className="h-96 animate-pulse rounded-lg bg-muted" />
      </div>
    );

  const photos = l.photos?.length ? l.photos : [];
  const requireAuth = () => {
    if (!user) {
      trackEvent("SIGN_UP_GATE_SHOWN", { reason: "LISTING_ACTION" });
      setGate({ reason: "LISTING_ACTION" });
      return false;
    }
    return true;
  };

  const toggleFav = async () => {
    if (!requireAuth()) return;
    try {
      if (l.is_favorite) await api.delete(`/favorites/${l.id}`);
      else await api.post(`/favorites/${l.id}`);
      setL({ ...l, is_favorite: !l.is_favorite });
      toast.success(l.is_favorite ? "Favorilerden çıkarıldı" : "Favorilere eklendi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendMessage = async () => {
    try {
      const { data } = await api.post("/conversations", { listing_id: l.id, text: msg });
      setMsgOpen(false);
      toast.success("Mesajınız gönderildi");
      navigate(`/panel/mesajlar?c=${data.conversation_id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendOffer = async () => {
    try {
      await api.post("/offers", {
        listing_id: l.id,
        price: Number(offer.price),
        quantity: Number(offer.quantity) || 1,
        note: offer.note,
        delivery_date: offer.delivery_date || null,
        transport_preference: offer.transport_preference || null,
      });
      setOfferOpen(false);
      toast.success("Teklifiniz gönderildi");
      navigate("/panel/teklifler");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendReport = async () => {
    try {
      await api.post("/complaints", { listing_id: l.id, type: report.type, description: report.description });
      setReportOpen(false);
      toast.success("Şikâyetiniz alındı, ekibimiz inceleyecek");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const specs = SPECS.filter(([k]) => l[k] !== null && l[k] !== undefined && l[k] !== "");
  const isOwner = user?.id === l.owner_id;
  // Tek dokunuşla WhatsApp: numarayı E.164'e çevir (0532... -> 90532..., 532... -> 90532...).
  const waNumber = (() => {
    let n = (l.whatsapp || "").replace(/[^0-9]/g, "");
    if (!n) return "";
    if (n.startsWith("90")) return n;
    if (n.startsWith("0")) return `90${n.slice(1)}`;
    if (n.length === 10 && n.startsWith("5")) return `90${n}`;
    return n;
  })();
  const waText = encodeURIComponent(
    `Merhaba, Ciftora'daki "${l.title}" ilanınız (İlan No: ${l.listing_no}) hakkında bilgi almak `
    + `istiyorum.\n${window.location.origin}/ilan/${l.id}`,
  );
  const waLink = `https://wa.me/${waNumber}?text=${waText}`;
  const showWhatsapp = !!waNumber && !l.is_demo && !isOwner;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 lg:px-8">
      <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr]">
        <div>
          {/* GALLERY */}
          <div className="relative overflow-hidden rounded-lg border border-border bg-card" data-testid="listing-gallery">
            <div className="relative aspect-[4/3] bg-muted">
              {photos.length ? (
                <img src={mediaUrl(photos[idx])} alt={l.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">Fotoğraf yok</div>
              )}
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)}
                    data-testid="gallery-prev"
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-background p-2.5 text-primary"
                    aria-label="Önceki"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setIdx((i) => (i + 1) % photos.length)}
                    data-testid="gallery-next"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-background p-2.5 text-primary"
                    aria-label="Sonraki"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            {photos.length > 1 && (
              <div className="no-scrollbar flex gap-2 overflow-x-auto p-3">
                {photos.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`h-16 w-20 shrink-0 overflow-hidden rounded border-2 ${i === idx ? "border-accent" : "border-transparent"}`}
                  >
                    <img src={mediaUrl(p)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* TITLE */}
          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                {l.listing_type}
              </span>
              {l.is_demo && (
                <span className="rounded-full border border-border px-3 py-1 text-xs font-semibold text-muted-foreground">
                  DEMO İLAN
                </span>
              )}
              {(isOwner || ["Moderator", "Admin", "SuperAdmin", "SupportAgent"].includes(user?.role)) && (
                <StatusPill status={l.status} />
              )}
            </div>
            <h1 className="mt-3 font-display text-2xl font-extrabold text-primary sm:text-3xl" data-testid="listing-title">
              {l.title}
            </h1>
            {l.is_demo && (
              <p className="mt-2 inline-block rounded-full border border-border bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground" data-testid="demo-badge-detail">
                Örnek İlan
              </p>
            )}
            {l.photo_disclaimer && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="photo-disclaimer">
                {l.photo_disclaimer}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {l.province} / {l.district}</span>
              <span className="inline-flex items-center gap-1"><Tag className="h-4 w-4" /> İlan No: {l.listing_no}</span>
              <span>{dateTR(l.created_at)}</span>
              <span>{l.views} görüntüleme</span>
            </div>
          </div>

          {/* CIFTORA DİJİTAL KAYIT ROZETİ */}
          {l.ciftora_records?.available && (
            <section className="mt-6 rounded-lg border border-secondary bg-secondary/5 p-5" data-testid="listing-digital-records-badge">
              <p className="font-display text-base font-extrabold text-secondary">
                Ciftora Dijital Kayıtları Mevcut
              </p>
              <ul className="mt-3 grid gap-1.5 text-sm text-primary sm:grid-cols-2">
                {[
                  ["has_vaccinations", "Aşı geçmişi mevcut"],
                  ["has_weights", "Tartım kayıtları mevcut"],
                  ["has_treatments", "Tedavi geçmişi mevcut"],
                  ["has_vet_records", "Veteriner kayıtları mevcut"],
                  ["has_documents", "Sağlık belgeleri mevcut"],
                ]
                  .filter(([k]) => l.ciftora_records[k])
                  .map(([k, label]) => (
                    <li key={k} data-testid={`digital-record-${k}`}>✓ {label}</li>
                  ))}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground" data-testid="digital-records-note">
                {l.ciftora_records.note} Satış sonrası satıcının izni ve sizin onayınızla kayıtlar
                Ciftora Çiftliğim hesabınıza aktarılabilir.
              </p>
            </section>
          )}

          {/* SPECS */}
          <section className="mt-8 rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-lg font-bold text-primary">Özellikler</h2>
            <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {specs.map(([k, label]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border/70 pb-2 text-sm">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="text-right font-semibold text-primary">{String(l[k])}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 flex flex-wrap gap-2">
              {BOOLS.filter(([k]) => l[k]).map(([k, label]) => (
                <span key={k} className="rounded-full border border-secondary bg-secondary/10 px-3 py-1 text-xs font-semibold text-secondary">
                  {label} ✓
                </span>
              ))}
            </div>
            {l.tag_number && (
              <p className="mt-4 text-xs text-muted-foreground">
                Küpe numarası gizlilik nedeniyle kısmen maskelenmiştir.
              </p>
            )}
          </section>

          {/* DESCRIPTION */}
          <section className="mt-6 rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-lg font-bold text-primary">Açıklama</h2>
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground md:text-base">
              {l.description || "Açıklama girilmemiş."}
            </p>
            {l.video_url && (
              <a href={l.video_url} target="_blank" rel="noreferrer" className="mt-4 inline-block text-sm font-semibold text-secondary hover:underline">
                Videoyu izle
              </a>
            )}
          </section>

          {l.moderation_note && isOwner && (
            <div className="mt-6 rounded-lg border border-[#B4531A] bg-[#B4531A]/10 p-5" data-testid="moderation-note">
              <p className="inline-flex items-center gap-2 font-display font-bold text-[#B4531A]">
                <AlertTriangle className="h-5 w-5" /> Moderasyon notu
              </p>
              <p className="mt-2 text-sm text-foreground">{l.moderation_note}</p>
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="font-display text-3xl font-extrabold text-primary" data-testid="listing-price">
              {money(l.price, l.currency)}
            </p>
            {l.negotiable && <p className="mt-1 text-sm text-muted-foreground">Pazarlığa açık</p>}
            <div className="mt-5 grid gap-3">
              {l.is_demo ? (
                <div className="rounded-lg border border-accent/50 bg-accent/10 p-4" data-testid="demo-listing-notice">
                  <p className="text-sm font-bold text-primary">Örnek İlan</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {l.demo_notice || "Bu ilan Ciftora özelliklerini göstermek amacıyla oluşturulmuş örnek ilandır."}
                  </p>
                  <Link
                    to="/ilanlar"
                    data-testid="demo-explore-market-btn"
                    className="mt-3 inline-flex h-12 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-bold text-primary-foreground hover:bg-secondary"
                  >
                    Ciftora Market'i Keşfet
                  </Link>
                </div>
              ) : (
              <>
              {!isOwner && (
                <>
                  <Button className="h-14 rounded-lg text-base font-bold" data-testid="message-seller-btn" onClick={() => requireAuth() && setMsgOpen(true)}>
                    <MessageSquare className="mr-2 h-5 w-5" /> Mesaj Gönder
                  </Button>
                  {showWhatsapp && (
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="whatsapp-quick-btn"
                      className="app-tap flex h-14 items-center justify-center gap-2 rounded-lg bg-[#25D366] text-base font-bold text-[#0B3D24]"
                    >
                      <MessageCircle className="h-5 w-5" /> WhatsApp'tan Yaz
                    </a>
                  )}
                  <Button
                    variant="outline"
                    className="h-14 rounded-lg border-2 text-base font-bold"
                    data-testid="make-offer-btn"
                    onClick={() => requireAuth() && setOfferOpen(true)}
                  >
                    Teklif Ver
                  </Button>
                </>
              )}
              {l.contact_phone ? (
                showPhone ? (
                  <div className="rounded-lg border border-border bg-background p-4 text-center" data-testid="phone-revealed">
                    <p className="font-display text-lg font-bold text-primary">{l.contact_phone}</p>
                    {l.whatsapp && (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="whatsapp-link"
                        className="mt-2 inline-block text-sm font-semibold text-secondary hover:underline"
                      >
                        WhatsApp ile yaz
                      </a>
                    )}
                  </div>
                ) : (
                  <Button variant="outline" className="h-14 rounded-lg text-base font-bold" data-testid="show-phone-btn" onClick={() => setShowPhone(true)}>
                    <Phone className="mr-2 h-5 w-5" /> Telefonu Göster
                  </Button>
                )
              ) : (
                <p className="text-sm text-muted-foreground">Satıcı telefon bilgisini paylaşmamış.</p>
              )}
              </>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="ghost" className="h-12" data-testid="detail-favorite-btn" onClick={toggleFav}>
                  <Heart className={`h-5 w-5 ${l.is_favorite ? "fill-destructive text-destructive" : ""}`} />
                </Button>
                <Button
                  variant="ghost"
                  className="h-12"
                  data-testid="share-btn"
                  onClick={() => {
                    navigator.clipboard?.writeText(window.location.href);
                    toast.success("Bağlantı kopyalandı");
                  }}
                >
                  <Share2 className="h-5 w-5" />
                </Button>
                <Button variant="ghost" className="h-12" data-testid="report-btn" onClick={() => requireAuth() && setReportOpen(true)}>
                  <Flag className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {l.seller && (
            <div className="rounded-lg border border-border bg-card p-6" data-testid="seller-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Satıcı</p>
              <p className="mt-2 font-display text-lg font-bold text-primary">
                {l.seller.first_name} {l.seller.last_name}
              </p>
              <p className="text-sm text-muted-foreground">
                {ROLE_LABELS[l.seller.role] || l.seller.role} · {l.seller.province}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Üyelik: {dateTR(l.seller.created_at)}</p>
              <div className="mt-4">
                <VerificationBadges verifications={l.seller.verifications} compact />
              </div>
              <div className="mt-3">
                <VerificationNote />
              </div>
              <Link to={`/satici/${l.seller.public_slug || l.seller.id}`} className="mt-4 inline-block text-sm font-semibold text-secondary hover:underline">
                Satıcının tüm ilanları
              </Link>
            </div>
          )}

          <div className="rounded-lg border border-secondary bg-secondary/10 p-5">
            <p className="inline-flex items-center gap-2 font-display font-bold text-secondary">
              <ShieldCheck className="h-5 w-5" /> Güvenli Ticaret
            </p>
            <p className="mt-2 text-sm text-foreground">
              Hayvanı ve belgelerini yerinde görmeden ödeme yapmayın. Kapora isteyen şüpheli
              mesajları şikâyet edin.
            </p>
          </div>
          {l.transport_available && (
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-secondary">
              <Truck className="h-4 w-4" /> Satıcı nakliye desteği sağlıyor
            </p>
          )}
        </aside>
      </div>

      {!!l.seller_other_listings?.length && (
        <Group title="Satıcının Diğer İlanları" items={l.seller_other_listings} testid="seller-others" />
      )}
      {!!l.similar?.length && <Group title="Benzer İlanlar" items={l.similar} testid="similar" />}

      {/* MOBİL YAPIŞIK AKSİYON ÇUBUĞU */}
      {!isOwner && !l.is_demo && createPortal(
        <div
          data-testid="mobile-listing-actions"
          style={{
            zIndex: "calc(var(--z-bottom-nav) - 1)",
            bottom: "calc(var(--mobile-bottom-nav-height) + env(safe-area-inset-bottom))",
          }}
          className="fixed left-0 right-0 grid grid-cols-2 gap-2 border-t border-border bg-background/95 px-3 py-2.5 backdrop-blur-md md:hidden"
        >
          <button
            data-testid="mobile-message-seller-btn"
            onClick={() => requireAuth() && setMsgOpen(true)}
            className="app-tap flex h-12 items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground"
          >
            <MessageSquare className="h-4 w-4" /> Mesaj Gönder
          </button>
          {l.contact_phone ? (
            <a
              href={`tel:${l.contact_phone.replace(/[^0-9+]/g, "")}`}
              data-testid="mobile-call-seller-btn"
              className="app-tap flex h-12 items-center justify-center gap-2 rounded-full border-2 border-primary text-sm font-bold text-primary"
            >
              <Phone className="h-4 w-4" /> Ara
            </a>
          ) : showWhatsapp ? (
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-whatsapp-btn"
              className="app-tap flex h-12 items-center justify-center gap-2 rounded-full bg-[#25D366] text-sm font-bold text-[#0B3D24]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          ) : (
            <button
              data-testid="mobile-offer-btn"
              onClick={() => requireAuth() && setOfferOpen(true)}
              className="app-tap flex h-12 items-center justify-center gap-2 rounded-full border-2 border-primary text-sm font-bold text-primary"
            >
              Teklif Ver
            </button>
          )}
        </div>,
        document.body,
      )}

      {/* MESSAGE DIALOG */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="bg-background">
          <DialogHeader><DialogTitle>Satıcıya mesaj gönder</DialogTitle></DialogHeader>
          <textarea
            data-testid="message-textarea"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={5}
            className="w-full rounded-lg border border-input bg-card p-3 text-base outline-none focus:border-secondary"
          />
          <DialogFooter>
            <Button className="h-12 w-full rounded-lg" data-testid="send-message-btn" onClick={sendMessage}>
              Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OFFER DIALOG */}
      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent className="bg-background">
          <DialogHeader><DialogTitle>Teklif ver</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <LabeledInput label="Teklif tutarı (TL)" testid="offer-price" type="number" value={offer.price} onChange={(v) => setOffer({ ...offer, price: v })} />
            <LabeledInput label="Adet" testid="offer-quantity" type="number" value={offer.quantity} onChange={(v) => setOffer({ ...offer, quantity: v })} />
            <LabeledInput label="Teslim tarihi" testid="offer-delivery" type="date" value={offer.delivery_date} onChange={(v) => setOffer({ ...offer, delivery_date: v })} />
            <LabeledInput label="Nakliye tercihi" testid="offer-transport" value={offer.transport_preference} onChange={(v) => setOffer({ ...offer, transport_preference: v })} />
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-primary">Not</label>
              <textarea
                data-testid="offer-note"
                value={offer.note}
                onChange={(e) => setOffer({ ...offer, note: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-input bg-card p-3 outline-none focus:border-secondary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button className="h-12 w-full rounded-lg" data-testid="send-offer-btn" onClick={sendOffer} disabled={!offer.price}>
              Teklifi Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPORT DIALOG */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-background">
          <DialogHeader><DialogTitle>İlanı şikâyet et</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-primary">Şikâyet türü</label>
              <select
                data-testid="report-type"
                value={report.type}
                onChange={(e) => setReport({ ...report, type: e.target.value })}
                className="h-12 w-full rounded-lg border border-input bg-card px-3 outline-none"
              >
                {COMPLAINT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-primary">Açıklama</label>
              <textarea
                data-testid="report-description"
                value={report.description}
                onChange={(e) => setReport({ ...report, description: e.target.value })}
                rows={4}
                className="w-full rounded-lg border border-input bg-card p-3 outline-none focus:border-secondary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="destructive" className="h-12 w-full rounded-lg" data-testid="send-report-btn" onClick={sendReport}>
              Şikâyeti Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SignupGateDialog gate={gate} onClose={() => setGate(null)}
        title="Satıcıyla iletişim üyelikle"
        description="Mesaj, teklif ve favori işlemleri için ücretsiz üyelik gerekiyor. Üyelikle ilan geçmişinizi ve tekliflerinizi tek yerde takip edersiniz." />
    </div>
  );
}

const Group = ({ title, items, testid }) => (
  <section className="mt-12" data-testid={`group-${testid}`}>
    <h2 className="font-display text-xl font-extrabold text-primary sm:text-2xl">{title}</h2>
    <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((i) => <ListingCard key={i.id} listing={i} />)}
    </div>
  </section>
);

const LabeledInput = ({ label, testid, value, onChange, type = "text" }) => (
  <div>
    <label className="mb-1.5 block text-sm font-semibold text-primary">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-12 w-full rounded-lg border border-input bg-card px-3 text-base outline-none focus:border-secondary"
    />
  </div>
);
